import React, { useEffect } from 'react';
import PropTypes from "prop-types";
import { useDispatch, useSelector } from 'react-redux';

import { ImposiumProjects, clearSelectedProject, selectProject } from "reducers/ImposiumProjects";

import EmptySection from "widgets/EmptySection";
import {
	DataGrid,
	Pager,
	Checkbox,
	ProgressIndicator,
	Toolbar,
	Button,
	Icon
} from "rhombus-react";
import Measure from 'react-measure';

import DateCell from "widgets/grid/cells/DateColumn";
import {
	MIN_GRID_HEIGHT,
	DEFAULT_ROW_HEIGHT,
	SEARCH_DEBOUNCE,
	IMPOSIUM_URL
} from 'utils/constants';

import { useCollectionFiltering, useGridScrollUtils } from "utils/DataGrid";

import "./ImposiumProjectsTable.css";

const IMPOSIUM_ICON_CLASS = "far fa-paintbrush-pencil";

const radioCell = ({ dependentValues }) => {
	const dispatch = useDispatch();
	const projectId = useSelector(state => state.imposiumProjects.selectedProject?.id);
	return (
		<Checkbox
			label=""
			name="test"
			id={dependentValues?.id}
			type="radio"
			value={dependentValues?.id}
			checked={dependentValues?.id === projectId}
			onChange={e => dispatch(selectProject(e.target.value))}
		/>
	);
};

const imposiumActionsCell = ({ dependentValues }) => {
	const projectId = dependentValues?.id;
	const organizationId = dependentValues?.organizationId;

	if (!projectId || !organizationId) {
		console.warn(`ImposiumProjectsTable: Cannot construct Imposium URL - projectId: ${projectId}, organizationId: ${organizationId}`);
		return null;
	}

	const imposiumUrl = `${IMPOSIUM_URL}/${projectId}?organization_id=${organizationId}`;

	return (
		<div className="imposium-project-actions-cell">
			<a
				className="action-link"
				data-tip="Open in Imposium"
				href={imposiumUrl}
				target="_blank"
				rel="noopener noreferrer"
			>
				<Icon name={IMPOSIUM_ICON_CLASS} />
			</a>
		</div>
	);
};

const projectNameCell = ({ value }) => {
	return (
		<div className="imposium-project-name-cell">
			<Icon className="imposium-row-icon" name={IMPOSIUM_ICON_CLASS} />
			<span>{value}</span>
		</div>
	);
};

export function ImposiumProjectsTable({ libraryId }) {
	const dispatch = useDispatch();
	const {
		isLoading,
		items,
		totalItems,
		totalPages,
		searchTerm,
		imposiumProjects,
	} = useSelector((state) => {
		return {
			isLoading: state.imposiumProjects.isLoading,
			items: state.imposiumProjects.items,
			totalItems: state.imposiumProjects.totalItems,
			totalPages: state.imposiumProjects.totalPages,
			searchTerm: state.imposiumProjects.filterInfo?.searchTerm || "",
			imposiumProjects: state.imposiumProjects,
		};
	});

	useEffect(() => {
		dispatch(clearSelectedProject());
		return () => {
			dispatch(ImposiumProjects.setSearchTerm(""));
		};
	}, []);

	const dataGridRef = useGridScrollUtils([items]);
	const {
		filterInfo: { page, rpp },
	} = useCollectionFiltering(
		imposiumProjects,
		ImposiumProjects.setAdvancedFilter,
		() => {
			if(libraryId){
				dispatch(ImposiumProjects.getImposiumProjects(libraryId));
			}
		}
	);

	const hasSearchTerm = !!searchTerm;
	const hasNoResults = !isLoading && items.length === 0;

	const clearSearch = () => dispatch(ImposiumProjects.setSearchTerm(""));

	const noProjectsEmptyProps = {
		className: "empty-imposium-projects",
		header: "No Imposium projects found",
		src: "/images/notification-icon.svg",
	};

	const noSearchResultsEmptyProps = {
		className: "empty-imposium-projects",
		header: "No search results to display",
		message: "Try adjusting your search to find what you are looking for",
		src: "/images/noSearch.svg",
		buttons: [<Button onClick={clearSearch} key="btn-clear">Clear Search</Button>],
	};

	const IMPOSIUM_PROJECT_COLUMNS = [
		{
			key: "idSelector",
			name: "",
			width: 60,
			getRowMetaData: data => data,
			formatter: radioCell,
		},
		{
			key: "name",
			name: "Project Name",
			sortable: false,
			resizable: false,
			formatter: projectNameCell,
		},
		{
			key: "actions",
			name: "Actions",
			width: 80,
			sortable: false,
			resizable: false,
			getRowMetaData: data => data,
			formatter: imposiumActionsCell,
		},
		{
			...DateCell,
			key: "lastModified",
			name: "Last Modified",
			width: 165,
			sortable: false,
			resizable: false,
		},
	];

	const dataGridProps = {
		columns: IMPOSIUM_PROJECT_COLUMNS,
		rowGetter: rowId => items[rowId],
		rowsCount: items.length,
		rowHeight: DEFAULT_ROW_HEIGHT,
	};

	const pagerProps = {
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: totalPages,
		maxItems: totalItems,
		disabled: isLoading,
		onPageChange: newPage => dispatch(ImposiumProjects.setPage(newPage)),
		onRowCountChange: newRPP => dispatch(ImposiumProjects.setRPP(newRPP)),
	};

	const toolbarProps = {
		search: true,
		onSearch: value => dispatch(ImposiumProjects.setSearchTerm(value)),
		className: "imposium-projects-search",
		searchDebounce: SEARCH_DEBOUNCE,
		searchValue: searchTerm,
		searchProps: { placeholder: "Search" },
	};

	const renderBody = (gridHeight) => {
		if (hasNoResults) {
			return (
				<EmptySection
					key="empty"
					{...(hasSearchTerm ? noSearchResultsEmptyProps : noProjectsEmptyProps)}
				/>
			);
		}
		return (
			<div ref={dataGridRef}>
				<DataGrid
					{...dataGridProps}
					minHeight={gridHeight}
				/>
			</div>
		);
	};

	return (
		<div className="imposium-projects-container">
			<ProgressIndicator isLoading={isLoading} />
			<Toolbar {...toolbarProps} />
			<div className="imposium-projects">
				<Measure>
					{(obj) => {
						let gridHeight = obj?.contentRect?.entry?.height || MIN_GRID_HEIGHT;

						return (
							<div className="measure-component" ref={obj.measureRef}>
								{renderBody(gridHeight)}
							</div>
						);
					}}
				</Measure>
				{!hasNoResults && <Pager {...pagerProps} />}
			</div>
		</div>
	);
}

ImposiumProjectsTable.displayName = "ImposiumProjectsTable";
ImposiumProjectsTable.propTypes = {
	libraryId: PropTypes.number.isRequired,
};

export default ImposiumProjectsTable;
